/**
 * Campus coordinates for map pins.
 * Only supported FL / NY (+ surrounding) schools are included.
 * Rendering ~70 DOM markers is cheap; we still prefer nearest-to-focus
 * so the map stays readable when zoomed into a metro.
 */

export interface UniversityCampus {
    id: number;
    name: string;
    abbreviation: string;
    longitude: number;
    latitude: number;
}

export const UNIVERSITY_CAMPUSES: UniversityCampus[] = [
    // Florida
    { id: 1, name: 'Florida A&M University', abbreviation: 'FAMU', longitude: -84.2866, latitude: 30.4275 },
    { id: 2, name: 'Florida Atlantic University', abbreviation: 'FAU', longitude: -80.1010, latitude: 26.3715 },
    { id: 3, name: 'Florida Gulf Coast University', abbreviation: 'FGCU', longitude: -81.7743, latitude: 26.4631 },
    { id: 4, name: 'Florida International University', abbreviation: 'FIU', longitude: -80.3739, latitude: 25.7571 },
    { id: 5, name: 'Florida State University', abbreviation: 'FSU', longitude: -84.3046, latitude: 30.4419 },
    { id: 6, name: 'University of Central Florida', abbreviation: 'UCF', longitude: -81.2001, latitude: 28.6024 },
    { id: 7, name: 'University of Florida', abbreviation: 'UF', longitude: -82.3431, latitude: 29.6436 },
    { id: 8, name: 'University of Miami', abbreviation: 'UM', longitude: -80.2787, latitude: 25.7215 },
    { id: 9, name: 'University of North Florida', abbreviation: 'UNF', longitude: -81.5093, latitude: 30.2690 },
    { id: 10, name: 'University of South Florida', abbreviation: 'USF', longitude: -82.4138, latitude: 28.0587 },
    { id: 11, name: 'University of West Florida', abbreviation: 'UWF', longitude: -87.2179, latitude: 30.5480 },
    { id: 12, name: 'Florida Polytechnic University', abbreviation: 'Florida Poly', longitude: -81.8490, latitude: 28.1485 },
    { id: 13, name: 'Nova Southeastern University', abbreviation: 'NSU', longitude: -80.2425, latitude: 26.0795 },
    { id: 14, name: 'Embry-Riddle Aeronautical University', abbreviation: 'ERAU', longitude: -81.0490, latitude: 29.1889 },
    { id: 15, name: 'Florida Institute of Technology', abbreviation: 'Florida Tech', longitude: -80.6335, latitude: 28.0655 },
    { id: 16, name: 'University of Tampa', abbreviation: 'UTampa', longitude: -82.4655, latitude: 27.9470 },
    { id: 17, name: 'Rollins College', abbreviation: 'Rollins', longitude: -81.3485, latitude: 28.5920 },
    { id: 18, name: 'Stetson University', abbreviation: 'Stetson', longitude: -81.3010, latitude: 29.0355 },
    { id: 19, name: 'New College of Florida', abbreviation: 'NCF', longitude: -82.5680, latitude: 27.3840 },
    { id: 20, name: 'Barry University', abbreviation: 'Barry', longitude: -80.1975, latitude: 25.8785 },
    { id: 21, name: 'Eckerd College', abbreviation: 'Eckerd', longitude: -82.6870, latitude: 27.7185 },
    { id: 22, name: 'Bethune-Cookman University', abbreviation: 'B-CU', longitude: -81.0305, latitude: 29.2105 },
    { id: 23, name: 'Florida Southern College', abbreviation: 'FSC', longitude: -81.9475, latitude: 28.0305 },
    { id: 24, name: 'Lynn University', abbreviation: 'Lynn', longitude: -80.1265, latitude: 26.3620 },
    { id: 25, name: 'Palm Beach Atlantic University', abbreviation: 'PBA', longitude: -80.0635, latitude: 26.7065 },
    { id: 26, name: 'St. Thomas University', abbreviation: 'STU', longitude: -80.2705, latitude: 25.9215 },
    { id: 27, name: 'Ringling College of Art and Design', abbreviation: 'Ringling', longitude: -82.5465, latitude: 27.3365 },
    { id: 28, name: 'Ave Maria University', abbreviation: 'Ave Maria', longitude: -81.6045, latitude: 26.3365 },

    // New York
    { id: 106, name: 'City University of New York', abbreviation: 'CUNY', longitude: -73.9645, latitude: 40.7685 },
    { id: 109, name: 'Columbia University', abbreviation: 'Columbia', longitude: -73.9626, latitude: 40.8075 },
    { id: 110, name: 'Cornell University', abbreviation: 'Cornell', longitude: -76.4735, latitude: 42.4534 },
    { id: 123, name: 'New York University', abbreviation: 'NYU', longitude: -73.9965, latitude: 40.7295 },
    { id: 131, name: 'Rochester Institute of Technology', abbreviation: 'RIT', longitude: -77.6748, latitude: 43.0845 },
    { id: 135, name: 'State University of New York', abbreviation: 'SUNY', longitude: -73.8240, latitude: 42.6480 },
    { id: 136, name: 'Syracuse University', abbreviation: 'Syracuse', longitude: -76.1363, latitude: 43.0377 },
    { id: 181, name: 'Fordham University', abbreviation: 'Fordham', longitude: -73.8855, latitude: 40.8615 },
    { id: 182, name: 'Pace University', abbreviation: 'Pace', longitude: -74.0055, latitude: 40.7115 },
    { id: 183, name: 'Stony Brook University', abbreviation: 'SBU', longitude: -73.1200, latitude: 40.9140 },
    { id: 184, name: 'University at Buffalo', abbreviation: 'UB', longitude: -78.7865, latitude: 43.0005 },
    { id: 185, name: 'Binghamton University', abbreviation: 'Binghamton', longitude: -75.9705, latitude: 42.0890 },
    { id: 186, name: 'University at Albany', abbreviation: 'UAlbany', longitude: -73.8250, latitude: 42.6860 },
    { id: 187, name: 'Hofstra University', abbreviation: 'Hofstra', longitude: -73.6005, latitude: 40.7145 },
    { id: 188, name: "St. John's University", abbreviation: "St. John's", longitude: -73.7945, latitude: 40.7220 },
    { id: 189, name: 'Pratt Institute', abbreviation: 'Pratt', longitude: -73.9615, latitude: 40.6915 },
    { id: 190, name: 'The New School', abbreviation: 'New School', longitude: -73.9955, latitude: 40.7355 },
    { id: 191, name: 'Fashion Institute of Technology', abbreviation: 'FIT', longitude: -73.9945, latitude: 40.7470 },
    { id: 192, name: 'Yeshiva University', abbreviation: 'YU', longitude: -73.9295, latitude: 40.8505 },
    { id: 193, name: 'Barnard College', abbreviation: 'Barnard', longitude: -73.9635, latitude: 40.8095 },
    { id: 194, name: 'Cooper Union', abbreviation: 'Cooper Union', longitude: -73.9905, latitude: 40.7295 },
    { id: 195, name: 'School of Visual Arts', abbreviation: 'SVA', longitude: -73.9845, latitude: 40.7385 },
    { id: 196, name: 'Rensselaer Polytechnic Institute', abbreviation: 'RPI', longitude: -73.6780, latitude: 42.7300 },
    { id: 197, name: 'Colgate University', abbreviation: 'Colgate', longitude: -75.5385, latitude: 42.8195 },
    { id: 198, name: 'Vassar College', abbreviation: 'Vassar', longitude: -73.8955, latitude: 41.6865 },
    { id: 199, name: 'Hamilton College', abbreviation: 'Hamilton', longitude: -75.4055, latitude: 43.0505 },
    { id: 200, name: 'Skidmore College', abbreviation: 'Skidmore', longitude: -73.7865, latitude: 43.0955 },
    { id: 201, name: 'Bard College', abbreviation: 'Bard', longitude: -73.9095, latitude: 42.0215 },
    { id: 202, name: 'Union College', abbreviation: 'Union', longitude: -73.9295, latitude: 42.8175 },
    { id: 203, name: 'Ithaca College', abbreviation: 'Ithaca', longitude: -76.4955, latitude: 42.4215 },
    { id: 204, name: 'Marist College', abbreviation: 'Marist', longitude: -73.9325, latitude: 41.7215 },
    { id: 205, name: 'Adelphi University', abbreviation: 'Adelphi', longitude: -73.6525, latitude: 40.7205 },
    { id: 206, name: 'Long Island University', abbreviation: 'LIU', longitude: -73.5915, latitude: 40.8175 },
    { id: 207, name: 'New York Institute of Technology', abbreviation: 'NYIT', longitude: -73.6005, latitude: 40.7465 },
    { id: 208, name: 'Mercy University', abbreviation: 'Mercy', longitude: -73.8345, latitude: 41.0355 },
    { id: 209, name: 'Manhattan University', abbreviation: 'Manhattan', longitude: -73.8975, latitude: 40.8895 },
    { id: 210, name: 'Sarah Lawrence College', abbreviation: 'Sarah Lawrence', longitude: -73.8445, latitude: 40.9345 },

    // Surrounding NJ / CT
    { id: 132, name: 'Rutgers University', abbreviation: 'Rutgers', longitude: -74.4510, latitude: 40.5005 },
    { id: 150, name: 'University of Connecticut', abbreviation: 'UConn', longitude: -72.2495, latitude: 41.8075 },
    { id: 211, name: 'Stevens Institute of Technology', abbreviation: 'Stevens', longitude: -74.0255, latitude: 40.7445 },
    { id: 212, name: 'Princeton University', abbreviation: 'Princeton', longitude: -74.6555, latitude: 40.3430 },
    { id: 213, name: 'New Jersey Institute of Technology', abbreviation: 'NJIT', longitude: -74.1785, latitude: 40.7425 },
    { id: 214, name: 'Seton Hall University', abbreviation: 'Seton Hall', longitude: -74.2455, latitude: 40.7425 },
    { id: 215, name: 'Yale University', abbreviation: 'Yale', longitude: -72.9225, latitude: 41.3163 },
    { id: 216, name: 'Fairfield University', abbreviation: 'Fairfield', longitude: -73.2365, latitude: 41.1605 },
];

const EARTH_RADIUS_MILES = 3958.8;

function haversineMiles(
    lng1: number,
    lat1: number,
    lng2: number,
    lat2: number,
): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
}

export interface NearestCampusOptions {
    /** Soft cap — enough for a metro without painting the whole East Coast. */
    limit?: number;
    /** Prefer campuses inside this radius; fill up to limit if fewer match. */
    maxMiles?: number;
    /** Always include these campus IDs when present. */
    alwaysIncludeIds?: number[];
}

/**
 * Pick campuses near a map focus.
 * ~70 markers is fine for Mapbox DOM cost; nearest keeps the map readable.
 */
export function getNearestCampuses(
    longitude: number,
    latitude: number,
    options: NearestCampusOptions = {},
): UniversityCampus[] {
    const limit = options.limit ?? 20;
    const maxMiles = options.maxMiles ?? 180;
    const alwaysInclude = new Set(options.alwaysIncludeIds || []);

    const ranked = UNIVERSITY_CAMPUSES
        .map((campus) => ({
            campus,
            miles: haversineMiles(longitude, latitude, campus.longitude, campus.latitude),
        }))
        .sort((a, b) => a.miles - b.miles);

    const selected = new Map<number, UniversityCampus>();

    for (const { campus, miles } of ranked) {
        if (selected.size >= limit) break;
        if (miles <= maxMiles || alwaysInclude.has(campus.id)) {
            selected.set(campus.id, campus);
        }
    }

    // If focus is sparse (e.g. middle of nowhere), still show nearest campuses.
    if (selected.size < Math.min(8, limit)) {
        for (const { campus } of ranked) {
            if (selected.size >= Math.min(12, limit)) break;
            selected.set(campus.id, campus);
        }
    }

    for (const id of Array.from(alwaysInclude)) {
        const campus = UNIVERSITY_CAMPUSES.find((c) => c.id === id);
        if (campus) selected.set(campus.id, campus);
    }

    return Array.from(selected.values());
}

export function getCampusByUniversityId(id: number): UniversityCampus | undefined {
    return UNIVERSITY_CAMPUSES.find((c) => c.id === id);
}

export interface MapBoundsLike {
    north: number;
    south: number;
    east: number;
    west: number;
}

/**
 * Campuses inside (padded) map bounds, nearest-to-center first.
 * Used when the user pans away from their search to browse other regions.
 */
export function getCampusesInBounds(
    bounds: MapBoundsLike,
    options: { limit?: number; padRatio?: number } = {},
): UniversityCampus[] {
    const limit = options.limit ?? 8;
    const padRatio = options.padRatio ?? 0.2;
    const latPad = Math.max((bounds.north - bounds.south) * padRatio, 0.15);
    const lngPad = Math.max((bounds.east - bounds.west) * padRatio, 0.15);
    const north = bounds.north + latPad;
    const south = bounds.south - latPad;
    const east = bounds.east + lngPad;
    const west = bounds.west - lngPad;
    const centerLat = (bounds.north + bounds.south) / 2;
    const centerLng = (bounds.east + bounds.west) / 2;

    return UNIVERSITY_CAMPUSES
        .filter(
            (campus) =>
                campus.latitude <= north &&
                campus.latitude >= south &&
                campus.longitude <= east &&
                campus.longitude >= west,
        )
        .map((campus) => ({
            campus,
            miles: haversineMiles(centerLng, centerLat, campus.longitude, campus.latitude),
        }))
        .sort((a, b) => a.miles - b.miles)
        .slice(0, limit)
        .map(({ campus }) => campus);
}
